import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { useUpdateBrandSettingsAction } from '@/hooks/actions/use-update-brand-settings-action';
import { Box, Button, createListCollection, Flex, Text } from '@chakra-ui/react';
import type { Brand } from '@fluenceur/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from './SettingsSection';

const SUPPORTED_CURRENCIES = [
  { label: 'EUR - Euro', value: 'EUR' },
  { label: 'USD - US Dollar', value: 'USD' },
  { label: 'GBP - British Pound', value: 'GBP' },
  { label: 'CAD - Canadian Dollar', value: 'CAD' },
];

interface CurrencyPreferenceSectionProps {
  brand: Brand;
  isShopifyConnected: boolean;
  isLast?: boolean;
}

const CurrencyPreferenceSection = ({
  brand,
  isShopifyConnected,
  isLast,
}: CurrencyPreferenceSectionProps) => {
  const { t } = useTranslation();
  const brandCurrency = brand.currency || 'EUR';
  const [selectedCurrency, setSelectedCurrency] = useState<string>(brandCurrency);
  const [originalCurrency, setOriginalCurrency] = useState<string>(brandCurrency);
  const updateSettings = useUpdateBrandSettingsAction(brand.id);

  const currencyCollection = useMemo(
    () => createListCollection({ items: SUPPORTED_CURRENCIES }),
    []
  );

  useEffect(() => {
    const nextCurrency = brand.currency || 'EUR';
    setSelectedCurrency(nextCurrency);
    setOriginalCurrency(nextCurrency);
  }, [brand.currency]);

  const savedCurrency = updateSettings.data?.data?.currency ?? updateSettings.variables?.currency;

  useEffect(() => {
    if (!updateSettings.isSuccess || !savedCurrency) {
      return;
    }
    setOriginalCurrency(savedCurrency);
    setSelectedCurrency(savedCurrency);
  }, [savedCurrency, updateSettings.isSuccess]);

  const handleSave = () => {
    updateSettings.mutate({ currency: selectedCurrency });
  };

  const hasChanges = selectedCurrency !== originalCurrency;

  return (
    <SettingsSection
      title={t('settings.brandCurrency.title')}
      description={t('settings.brandCurrency.description')}
      isLast={isLast}
    >
      <Box maxW="400px" w="full">
        <SelectRoot
          collection={currencyCollection}
          value={[selectedCurrency]}
          onValueChange={(e) => setSelectedCurrency(e.value[0] ?? '')}
          size="md"
          disabled={isShopifyConnected}
        >
          <SelectTrigger>
            <SelectValueText />
          </SelectTrigger>
          <SelectContent>
            {currencyCollection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </Box>

      {isShopifyConnected ? (
        <Text fontSize="sm" color="fg.muted" mt={3}>
          {t('settings.brandCurrency.shopifySynced')}
        </Text>
      ) : (
        <Flex gap={2} mt={4}>
          <Button
            size="sm"
            disabled={!hasChanges || updateSettings.isPending}
            onClick={handleSave}
            loading={updateSettings.isPending}
          >
            {t('settings.brandCurrency.save')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelectedCurrency(originalCurrency)}
            disabled={!hasChanges || updateSettings.isPending}
          >
            {t('common.cancel')}
          </Button>
        </Flex>
      )}
    </SettingsSection>
  );
};

export default CurrencyPreferenceSection;
